import {
  ChannelTypesEnum,
  SolutionEnvironmentsEnum,
  proStudioUrqlGql as urqlGql,
} from '@pypestream/api-services';
import { reportDegradedLoad } from './helpers/report-degraded-load';
import { throwErrorWithId } from '@pypestream/utils';

import { ProStudioEvents, ProStudioContext } from '../prostudio.xstate.types';
import { Pype } from '../prostudio.api-service.types';
import {
  checkForErrors,
  resolveBotSolutionIds,
  resolveSolutionId,
} from '../../utils';
import { CONTACT_CENTER_INTEGRATION_NAME } from '../../utils/mutate-channel';
import { loadBotPypes } from './load-bot-pypes';
import {
  ApplySurveyLinkInheritanceParams,
  applySurveyLinkInheritance,
  planSurveyLinkInheritance,
} from './helpers/solutions';

export const createChannel = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{ botPypes: Pype[] } | void> => {
  if (event.type !== 'createChannel') {
    return;
  }

  const { cb, errorCb, ...channelProps } = event.payload;

  // Any failure — context validation OR the mutation — must settle the caller's
  // promise via errorCb before rethrowing, otherwise "Save and leave" hangs
  // forever on "Saving…". Rethrow still routes the machine to botFlowError.
  try {
    const { project, botName, customerId } = ctx;

    const uniqueCustomerId = project?.projectProStudioSetting?.customerId;

    if (!uniqueCustomerId) {
      throwErrorWithId('Customer ID is required');
      return;
    }

    if (!botName) {
      throwErrorWithId('Bot Name is required');
      return;
    }

    if (!customerId) {
      throwErrorWithId('Customer ID is required');
      return;
    }

    // The bare solution uuid: the backend composes `<customerName>.<uuid>` from it for
    // the stream's start_chat_bot_id. `${customerId}.${botName}` came out as
    // `<customer>.<customer>.<name>`, which no pype read ever matched.
    const botId = resolveSolutionId(ctx);

    if (!botId) {
      throwErrorWithId('Solution ID is required');
      return;
    }

    const payload = {
      beta: JSON.stringify(channelProps.beta),
      botId,
      botName,
      customerId: uniqueCustomerId,
      customPypeCss: channelProps.customPypeCss,
      name: channelProps.name,
      style: JSON.stringify(channelProps.style),
      env:
        channelProps.env === 'live'
          ? SolutionEnvironmentsEnum.Live
          : SolutionEnvironmentsEnum.Sandbox,

      type:
        channelProps.type === 'twilio_voice'
          ? ChannelTypesEnum.Voice
          : ChannelTypesEnum.Default,
      twilioParams: channelProps.voiceAiConfig,
      config: {
        agentRouting: channelProps.config?.agent_routing,
        // Only send integration fields when Contact Center is actually being
        // configured (account_id / inbox_id present). Otherwise omit them so
        // the backend doesn't persist empty headers that collide with another
        // platform integration on the pype.
        ...(channelProps.config?.config?.integration_headers?.account_id ||
        channelProps.config?.config?.integration_headers?.inbox_id
          ? {
              integrationHeadersAccountId:
                channelProps.config?.config?.integration_headers?.account_id,
              integrationHeadersInboxId:
                channelProps.config?.config?.integration_headers?.inbox_id,
              integrationName: CONTACT_CENTER_INTEGRATION_NAME,
            }
          : {}),
      },
    };

    const createChannelResponse = await urqlGql.createChannel(payload);
    checkForErrors(createChannelResponse);
  } catch (error) {
    errorCb?.();
    throw error;
  }

  cb?.();

  const { botPypes } = await loadBotPypes(ctx, event);

  // PSTUDIO-3928: the survey→main link lives on a pype STREAM and is written ONLY
  // when the SURVEY is published, stamping the streams that existed at that moment.
  // The channel just created therefore has an EMPTY end_chat_bot_id and runs no
  // survey until the survey is republished — so the fresh stream inherits the link
  // its already-linked siblings carry.
  //
  // BEST-EFFORT (approved decision, option A): the channel IS created and `cb`
  // has already settled the caller's promise, so nothing here may reject — a
  // rejection would route the machine to '#botFlowError' and report a successful
  // creation as a failure. Everything below is wrapped, and a failure only warns.
  try {
    // The SAME resolver loadBotPypes uses to scope the pype read, so the plan and
    // the pypes it is planned from agree on which main this is. Re-deriving it as
    // `${customerId}.${botName}` would be a second, silently divergent source.
    // Every id this main's streams may carry: a legacy main's older channels keep its
    // `botSolutionId`, and their survey links are what a new channel inherits.
    const mainStartChatIds = resolveBotSolutionIds(ctx);
    // Scoped to the env the channel was created in: the read covers both envs, and
    // creating a Sandbox channel must not stamp an unlinked LIVE stream.
    const plan = planSurveyLinkInheritance(
      botPypes,
      mainStartChatIds,
      channelProps.env === 'live' ? 'live' : 'sandbox'
    );

    if (plan.length) {
      const { userInfo } = ctx;
      const uniqueCustomerId = ctx.project?.projectProStudioSetting?.customerId;
      const projectId = ctx.project?.id;
      const hasUserInfo =
        !!userInfo?.id &&
        !!userInfo?.email &&
        !!userInfo?.firstName &&
        !!userInfo?.lastName;

      if (!uniqueCustomerId || !projectId || !hasUserInfo) {
        // Every other failing path here warns; a plan built and then dropped for
        // missing audit inputs must not be the one case that leaves no trace.
        console.warn(
          `[createChannel] survey link inheritance skipped for ${plan.length} stream(s): incomplete context (customerId/projectId/userInfo)`
        );
        return { botPypes };
      }

      const stamped = await applySurveyLinkInheritance({
        plan,
        uniqueCustomerId,
        projectId,
        userInfo: userInfo as ApplySurveyLinkInheritanceParams['userInfo'],
      });

      if (!stamped) {
        // Nothing was written, so a second full read would surface nothing new —
        // and the executor's own warnings already named each failed stream.
        console.warn(
          `[createChannel] survey link inheritance stamped 0 of ${plan.length} stream(s); the channel was created without it`
        );
        return { botPypes };
      }

      // Re-read so ctx.botPypes reflects the link that was just written:
      // `onDoneAssignContext` assigns `data.botPypes` straight into context, and
      // returning the pre-write read would leave the UI showing the very gap
      // this step just closed.
      //
      // The ctx handed over carries the FRESH botPypes, not the invoke's original
      // snapshot: `loadBotPypes` does not reject once its retries are exhausted —
      // it resolves with the `botPypes` of the ctx it was given. Passing `ctx` here
      // would therefore make a degraded read resolve to the list from BEFORE this
      // channel existed, and returning that wipes the new channel out of the table
      // while the success toast still claims it was created.
      const { botPypes: rereadBotPypes } = await loadBotPypes(
        { ...ctx, botPypes },
        event
      );
      return { botPypes: rereadBotPypes };
    }
  } catch (error) {
    console.warn(
      '[createChannel] survey link inheritance did not complete; the channel was created and may be missing its survey link',
      error
    );
    // The channel WAS created and the caller's promise has already settled, so
    // this block cannot reject — which also means the shared reporter never
    // sees it. What is lost is silent and long-lived: the new channel runs no
    // survey until someone republishes one, and nothing anywhere says so.
    reportDegradedLoad(error, 'create-channel:survey-link-inheritance', {
      projectId: ctx.project?.id,
    });
  }

  return { botPypes };
};
